/**
 * Auth API client — login, logout, me, preferences, admin CRUD.
 */
import { withCsrfHeader } from "./csrf";
import type { BrainSnapshotShape } from "../store/slices/brainSlice";

export interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  role: string;
  avatar_url: string | null;
  company: string | null;
  title: string | null;
  preferences: UserPreferences | null;
}

export interface UserPreferences {
  /**
   * Kullanıcıya özel tercihler. 2026-07-25 ayar denetiminden sonra bu tip
   * kasten YALNIZCA backend'in \PreferencesUpdate\ şemasının gerçekten
   * kabul ettiği alanları içerir.
   *
   * Kaldırılan \llm\ ve \	ts\ blokları FE'den gönderiliyordu ama backend
   * şemasında karşılıkları YOKTU — Pydantic sessizce atıyordu, veritabanına
   * hiç yazılmıyorlardı. Bunların doğru yeri Yönetim → Sistem Ayarları
   * (\system_config\, global kapsam). \defaults\ bloğu ise yazılıyor ama
   * okuyan yoktu (wizard \WIZARD_DEFAULTS\ sabitini kullanıyor).
   */
  debate_prompt?: string;
  moderator_prompt?: string;
  display?: {
    theme?: string;
    stage_shape?: string;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AdminUserListItem {
  id: string;
  email: string;
  display_name: string;
  role: string;
  is_active: boolean;
  company: string | null;
  title: string | null;
  created_at: string;
  last_login_at: string | null;
}

export interface CreateUserPayload {
  email: string;
  password: string;
  display_name: string;
  role?: string;
  company?: string;
  title?: string;
}

export interface UpdateUserPayload {
  display_name?: string;
  role?: string;
  is_active?: boolean;
  company?: string;
  title?: string;
  password?: string;
}

export interface DebateHistoryItem {
  id: string;
  query: string;
  topology: string;
  status: string;
  persona_count: number;
  turn_count: number | null;
  consensus_score: number | null;
  simulation_score: number | null;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  created_at: string;
  // ── Versioning lineage ────────────────────────────────────────────
  // Populated by the backend so the FE can render version chips,
  // group siblings, and offer "compare with parent" without an extra
  // network round-trip per row.
  parent_debate_id?: string | null;
  version_label?: string | null;
  /** 1-based version number within the lineage. Original = 1. */
  version_index?: number;
  /** `original` (root) | `fork` (with overrides) | `duplicate` (independent copy) */
  derived_from?: "original" | "fork" | "duplicate" | string;
  /** Total number of debates sharing the same root. 1 ⇒ standalone. */
  version_count?: number;
  /** Stable identifier of the root debate of this lineage. */
  root_debate_id?: string | null;
  // ── Run-time configuration snapshot ──────────────────────────────
  // Surfaced so the rerun modal can prefill its sliders with the
  // exact parent settings, not generic defaults.
  max_rounds?: number | null;
  agent_count?: number | null;
}

export interface ReplayPersona {
  id: string;
  display_name: string;
  role: string;
  expertise: string | null;
  theme_color: string | null;
  voice_id: string | null;
  /** Optional fields surfaced for the avatar pipeline (replay → PDF). */
  honorific?: string | null;
  gender?: string | null;
  avatar_url?: string | null;
}

export interface ReplayTurnCritique {
  targetTurnId?: string;
  flaw?: string;
  severity?: string;
  [k: string]: unknown;
}

export interface ReplaySourceItem {
  name: string;
  title: string;
  url: string;
  summary?: string;
  published?: string | null;
  position?: number;
}

export interface ReplayRagCitation {
  doc_id: string;
  filename: string;
  chunk_idx: number;
  score: number;
  scope: "pool" | "private";
  excerpt: string;
  position?: number;
}

export interface ReplayTurn {
  id: string;
  speaker_id: string;
  round_index: number;
  phase: string;
  critique: ReplayTurnCritique | null;
  final_text: string | null;
  token_count: number | null;
  char_count: number | null;
  duration_ms: number | null;
  started_at: string;
  ended_at: string | null;
  /** External web hits the speaker leaned on. Empty for legacy/no-research turns. */
  sources?: ReplaySourceItem[];
  /** Operator-uploaded document chunks injected into this speaker's prompt. */
  rag_citations?: ReplayRagCitation[];
}

export interface ReplaySourceRollup {
  name: string;
  title: string;
  url: string;
  summary?: string;
  published?: string | null;
  cited_in_turns: string[];
}

export interface ReplayDocumentRollup {
  doc_id: string;
  filename: string;
  scope: "pool" | "private";
  max_score: number;
  chunk_count: number;
  chunks: ReplayRagCitation[];
  cited_in_turns: string[];
}

/**
 * Replay payload action plan — DB raw JSONB shape. Two flavours coexist
 * here on purpose:
 *   • Legacy 3-tier `string[]` arrays (pre-2026 verdicts).
 *   • New 5-horizon structured arrays (`{title,horizon,...}` objects).
 *
 * The `unknown[]` typing is intentional — callers MUST funnel the
 * payload through the backend `ActionPlan` Pydantic validator (or the
 * FE `coerceActionPlan` helper) before rendering, so legacy strings
 * are upgraded into structured `ActionItem` rows.
 */
export interface ReplayActionPlan {
  immediate?: unknown[];
  tactical?: unknown[];
  strategic?: unknown[];
  architectural?: unknown[];
  governance?: unknown[];
  risks?: unknown[];
  kpis?: unknown[];
  criticalAssumptions?: string[];
  critical_assumptions?: string[];
  [k: string]: unknown;
}

export interface ReplayDissent {
  agentId?: string;
  concern?: string;
  severity?: string;
  [k: string]: unknown;
}

export interface ReplaySimulationOutcome {
  scenario?: string;
  survived?: boolean;
  rationale?: string;
  [k: string]: unknown;
}

export interface ReplayVerdict {
  executive: string;
  dissenting: ReplayDissent[];
  action_plan: ReplayActionPlan;
  simulation_score: number | null;
  simulation_outcomes: ReplaySimulationOutcome[] | null;
  commentary_analysis?: string | null;
  commentary_critique?: string | null;
  /**
   * Görev 18-24 + Karar Paketi artefaktları. Backend (`routes_history.py`
   * `DebateReplayVerdict`) bunları uzun süredir gönderiyordu; bu arayüzde
   * tanımlı olmadıkları için replay yolundan üretilen PDF, canlı
   * oturumdan üretilenden bile eksik çıkıyordu.
   */
  coverage_matrix?: Record<string, unknown> | null;
  entity_graph?: Record<string, unknown> | null;
  consistency_flags?: Record<string, unknown>[] | null;
  investigation_plan?: Record<string, unknown> | null;
  resilience_status?: string | null;
  baseline_simulation_score?: number | null;
  hardened_simulation_score?: number | null;
  simulation_runs?: Record<string, unknown>[] | null;
  hardened_action_plan?: Record<string, unknown> | null;
  board_brief?: Record<string, unknown> | null;
  decision_memory?: Record<string, unknown> | null;
  bluf?: Record<string, unknown> | null;
  decision_manifest?: Record<string, unknown> | null;
  verdict_status?: Record<string, unknown> | null;
  decision_type?: Record<string, unknown> | null;
  evidence_conflicts?: Record<string, unknown>[] | null;
  confidence_metrics?: Record<string, unknown> | null;
  evidence_bibliography?: Record<string, unknown>[] | null;
  evidence_quality?: string | null;
}

export interface ConsensusTrajectoryPoint {
  round: number;
  score: number; // 0-100
  deadlock: boolean;
  timestamp: string;
}

export interface PersonaContributionStats {
  persona_id: string;
  turn_count: number;
  total_chars: number;
  total_tokens: number;
  avg_chars_per_turn: number;
  critique_count: number;
}

export interface DebateReplayData {
  session: DebateHistoryItem;
  personas: ReplayPersona[];
  turns: ReplayTurn[];
  verdict: ReplayVerdict | null;
  consensus_trajectory: ConsensusTrajectoryPoint[];
  persona_stats: PersonaContributionStats[];
  /** Deduplicated bibliography of every external source any speaker cited. */
  sources_rollup?: ReplaySourceRollup[];
  /** Deduplicated catalogue of every uploaded document the council leaned on. */
  documents_rollup?: ReplayDocumentRollup[];
  /**
   * AgentBrain bilişsel iz — `personaId → [snapshot, ...]`, tur sırasıyla.
   * Canlı tartışmada bu paneli `brain.update` SSE event'i besler; stream
   * kapandıktan sonra tek kaynak budur. Beyin motoru kapalıyken koşmuş
   * oturumlarda boş gelir.
   */
  brain_snapshots?: Record<string, BrainSnapshotShape[]>;
}

export interface V2ResilienceSnapshot {
  session_id: string;
  status: string;
  schema_version: number;
  baseline_simulation_score?: number | null;
  hardened_simulation_score?: number | null;
  simulation_runs?: Record<string, unknown>[];
  hardened_action_plan?: Record<string, unknown> | null;
  confidence_metrics?: Record<string, unknown> | null;
  board_brief?: Record<string, unknown> | null;
  decision_memory?: Record<string, unknown> | null;
}

const BASE = "/api/debate";

/**
 * Hata, HTTP durumunu ve arka ucun hata kodunu TAŞIR.
 *
 * Önceden yalnız `new Error(mesaj)` fırlatılıyordu; çağıranlar "bu bir
 * oturum hatası mı yoksa gerçek bir arıza mı" sorusunu mesaj metninde
 * alt dize arayarak yanıtlamak zorundaydı. Arka uç 401'i
 * `"missing session credential"` diye döndürdüğü için o arama tutmuyor
 * ve giriş ekranı, kullanıcı daha hiçbir şey yapmadan kırmızı bir
 * İngilizce hata rozeti gösteriyordu.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code = "") {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = withCsrfHeader({
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  });

  const res = await fetch(url, { ...init, headers, credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = body?.message || body?.detail || `HTTP ${res.status}`;
    throw new ApiRequestError(String(msg), res.status, String(body?.code ?? ""));
  }
  return res.json() as Promise<T>;
}

// ─── Auth ─────────────────────────────────────────────────────────────────

export async function login(creds: LoginCredentials): Promise<UserProfile> {
  return request<UserProfile>(`${BASE}/auth/login`, {
    method: "POST",
    body: JSON.stringify(creds),
  });
}

export async function logout(): Promise<void> {
  await request<{ status: string }>(`${BASE}/auth/logout`, { method: "POST" });
}

export async function fetchMe(): Promise<UserProfile> {
  return request<UserProfile>(`${BASE}/auth/me`);
}

export async function updatePreferences(
  prefs: Partial<UserPreferences>,
): Promise<{ status: string; preferences: UserPreferences }> {
  return request(`${BASE}/auth/preferences`, {
    method: "PUT",
    body: JSON.stringify(prefs),
  });
}

// ─── Admin ────────────────────────────────────────────────────────────────

export async function adminListUsers(): Promise<AdminUserListItem[]> {
  return request<AdminUserListItem[]>(`${BASE}/admin/users`);
}

export async function adminCreateUser(
  payload: CreateUserPayload,
): Promise<AdminUserListItem> {
  return request<AdminUserListItem>(`${BASE}/admin/users`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateUser(
  userId: string,
  payload: UpdateUserPayload,
): Promise<{ status: string }> {
  return request(`${BASE}/admin/users/${userId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function adminDeleteUser(
  userId: string,
): Promise<{ status: string }> {
  return request(`${BASE}/admin/users/${userId}`, { method: "DELETE" });
}

// ─── System Config (Admin) ────────────────────────────────────────────────

export interface SystemConfig {
  llm_default_model?: string;
  llm_temperature?: number;
  llm_top_p?: number;
  llm_max_tokens?: number;
  llm_presence_penalty?: number;
  llm_frequency_penalty?: number;
  /**
   * Model id used by the Editorial Commentary subsystem.
   * `null` / unset ⇒ falls back to the catalogue default.
   */
  commentary_model_id?: string | null;
  /**
   * Model id used by the AI Comparison report subsystem.
   * `null` / unset ⇒ falls back to the catalogue default.
   */
  comparison_model_id?: string | null;
  /**
   * P2 — VoxCPM2/Chatterbox (GPU) and ElevenLabs (cloud) engines were
   * removed entirely; only the native tr-TR Edge-TTS engine remains,
   * so `tts_engine` now only accepts `edge-tts` / `off`.
   */
  tts_engine?: "edge-tts" | "off";
  tts_speed?: number;
  tts_disabled?: boolean;
  /**
   * Brave Search API key — İş Kolu D1. GET returns a masked
   * "••••1234" display value (never the raw secret); PUT-ing that
   * same masked value back is a documented no-op on the backend
   * (it strips the key from the update so it never overwrites the
   * real value with the mask).
   */
  research_brave_api_key?: string | null;
  /**
   * İstihbarat Ajanı Faz 0 — Google Programmable Search Engine credentials.
   * Same masked-GET / no-op-remask-PUT contract as `research_brave_api_key`.
   */
  research_google_cse_key?: string | null;
  research_google_cse_cx?: string | null;
  /** İstihbarat Ajanı Faz 0 — Yandex XML Search API credentials. */
  research_yandex_api_key?: string | null;
  research_yandex_user?: string | null;
  /** İstihbarat Ajanı Faz 5 — capability feature-flags + budgets. */
  intel_vision_enabled?: boolean;
  intel_entity_follow_enabled?: boolean;
  intel_dossier_enabled?: boolean;
  intel_screenshot_enabled?: boolean;
  intel_pdf_reader_enabled?: boolean;
  intel_max_images_per_turn?: number;
  intel_max_entity_follow_depth?: number;
  // 2026-07-25 ayar denetimi — \parameter_catalog'tan buraya taşındı.
  default_max_rounds?: number;
  hard_cap_rounds?: number;
  cheap_model?: string;
  // ---- ALTYAPI (2026-07-25 denetimi) ----
  // Sadece SICAK YÜKLENEBİLİR alanlar. Boot'ta tüketilenler
  // (database_url, embed_model, havuz limitleri) buraya EKLENMEZ;
  // /admin/infrastructure GET yanıtında salt okunur gösterilirler.
  qdrant_url?: string;
  qdrant_collection?: string;
  qdrant_timeout_s?: number;
  extractor_url?: string;
  extractor_token?: string;
  extractor_timeout_s?: number;
  embed_base_url?: string;
  embed_api_key?: string;
  rerank_base_url?: string;
  rerank_enabled?: boolean;
  rerank_min_score?: number;
  research_url?: string;
  stealth_fetch_base_url?: string;
  stealth_fetch_auth_token?: string;
  searxng_self_hosted_url?: string;
  research_disable_ddg?: boolean;
  rag_chunk_chars?: number;
  rag_chunk_overlap?: number;
  rag_top_k?: number;
  rag_max_upload_bytes?: number;
}

export interface InfraField {
  value: string | number | boolean | null;
  stored: boolean;
  secret: boolean;
}

export interface InfraReadonly {
  key: string;
  label: string;
  value: string | number | boolean | null;
}

export interface InfrastructureSnapshot {
  editable: Record<string, InfraField>;
  readonly: InfraReadonly[];
  restartRequired: string[];
}

export interface InfraProbeResult {
  ok: boolean;
  status: number | null;
  latencyMs: number;
  error: string | null;
  detail?: string;
}

export async function getInfrastructure(): Promise<InfrastructureSnapshot> {
  return request<InfrastructureSnapshot>(`${BASE}/admin/infrastructure`);
}

export async function probeInfrastructure(
  targets: string[] = [],
): Promise<{ results: Record<string, InfraProbeResult>; allOk: boolean }> {
  return request(`${BASE}/admin/infrastructure/probe`, {
    method: "POST",
    body: JSON.stringify({ targets }),
  });
}

// --- Kanit Havuzu (research corpus) ---

export interface CorpusStats {
  total: number;
  domains: number;
  sightings: number;
  reuses: number;
  chars: number;
  unembedded: number;
  expired: number;
  redundancy_pct: number;
  by_class: Record<string, number>;
  top_domains: Array<{ domain: string; count: number; reuses: number }>;
}

export interface CorpusItem {
  url: string;
  title: string;
  summary: string;
  domain: string;
  sourceName: string;
  domainTier: number;
  stalenessClass: string;
  charCount: number;
  hitCount: number;
  reuseCount: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  expiresAt: string | null;
  embedded: boolean;
}

export async function getCorpusStats(scope = "all"): Promise<CorpusStats> {
  return request<CorpusStats>(BASE + "/admin/research-corpus/stats?scope=" + scope);
}

export async function listCorpus(params: {
  q?: string;
  domain?: string;
  stalenessClass?: string;
  scope?: string;
  limit?: number;
  offset?: number;
  // `count` is this page's length; `total` is the full filtered match count and
  // is what the pager sizes itself from.
} = {}): Promise<{ items: CorpusItem[]; count: number; total: number }> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.domain) qs.set("domain", params.domain);
  if (params.stalenessClass) qs.set("staleness_class", params.stalenessClass);
  qs.set("scope", params.scope ?? "all");
  qs.set("limit", String(params.limit ?? 50));
  qs.set("offset", String(params.offset ?? 0));
  return request(BASE + "/admin/research-corpus?" + qs.toString());
}

export async function pruneCorpus(): Promise<{ removed: number }> {
  return request(BASE + "/admin/research-corpus/prune", { method: "POST" });
}

export async function embedCorpus(limit = 64): Promise<{ embedded: number }> {
  return request(BASE + "/admin/research-corpus/embed?limit=" + limit, {
    method: "POST",
  });
}

export async function getSystemConfig(): Promise<SystemConfig> {
  const res = await request<{ config: SystemConfig }>(`${BASE}/admin/system-config`);
  return res.config;
}

export async function updateSystemConfig(
  payload: SystemConfig,
): Promise<{ status: string }> {
  return request(`${BASE}/admin/system-config`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// ─── v2 Configuration-as-Data (Admin) ─────────────────────────────────────

export interface V2ParameterCatalogItem {
  key: string;
  category: string;
  value_type: string;
  default_value: Record<string, unknown>;
  min_value: number | null;
  max_value: number | null;
  validation_regex: string | null;
  visibility: string;
  description: string;
  schema_version: number;
}

export interface V2ParameterValue {
  key: string;
  scope_type: string;
  scope_id: string;
  value: Record<string, unknown>;
  set_by: string;
  set_at: string;
}

export async function adminListV2Parameters(): Promise<V2ParameterCatalogItem[]> {
  return request<V2ParameterCatalogItem[]>(`${BASE}/v2/admin/parameters`);
}

export async function adminListV2ParameterValues(): Promise<V2ParameterValue[]> {
  return request<V2ParameterValue[]>(`${BASE}/v2/admin/parameters/values`);
}

export async function adminUpdateV2Parameter(
  key: string,
  payload: {
    scope_type?: "system" | "tenant" | "debate" | "operator";
    scope_id?: string;
    value: Record<string, unknown>;
  },
): Promise<V2ParameterValue> {
  return request<V2ParameterValue>(`${BASE}/v2/admin/parameters/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// ─── Prompt Templates (Admin) — İş Kolu F ─────────────────────────────────

export interface PromptTemplate {
  key: string;
  version: number;
  body: string;
  category: string;
  description: string;
  is_active: boolean;
  updated_by: string | null;
  updated_at: string;
  has_default: boolean;
  is_customized: boolean;
}

export interface PromptTemplateHistoryEntry {
  version: number;
  body: string;
  is_active: boolean;
  updated_by: string | null;
  updated_at: string;
}

export async function adminListPromptTemplates(): Promise<PromptTemplate[]> {
  return request<PromptTemplate[]>(`${BASE}/v2/admin/prompt-templates`);
}

export async function adminGetPromptTemplateHistory(key: string): Promise<PromptTemplateHistoryEntry[]> {
  return request<PromptTemplateHistoryEntry[]>(`${BASE}/v2/admin/prompt-templates/${encodeURIComponent(key)}/history`);
}

export async function adminUpdatePromptTemplate(key: string, body: string): Promise<PromptTemplate> {
  return request<PromptTemplate>(`${BASE}/v2/admin/prompt-templates/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify({ body }),
  });
}

export async function adminRevertPromptTemplate(key: string): Promise<PromptTemplate> {
  return request<PromptTemplate>(`${BASE}/v2/admin/prompt-templates/${encodeURIComponent(key)}/revert`, {
    method: "POST",
  });
}

// ─── LLM Models (Admin) ───────────────────────────────────────────────────

export interface LlmModel {
  id: string;
  label: string;
  family: string;
  backend_model: string;
  base_url: string;
  api_key: string;
  context_len: number;
  speed_tier: string;
  description: string;
  supports_tool_call: boolean;
  supports_streaming: boolean;
  supports_vision: boolean;
  supports_reasoning: boolean;
  tool_call_parser: string | null;
  reasoning_parser: string | null;
  default_temperature: number;
  default_top_p: number;
  default_max_tokens: number;
  default_presence_penalty: number;
  default_frequency_penalty: number;
  fits: string[];
  sort_order: number;
  is_active: boolean;
  is_default: boolean;
  is_fast_route: boolean;
  is_speaking_model: boolean;
}

export async function adminListModels(): Promise<LlmModel[]> {
  return request<LlmModel[]>(`${BASE}/admin/models`);
}

export async function adminCreateModel(payload: LlmModel): Promise<LlmModel> {
  return request<LlmModel>(`${BASE}/admin/models`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateModel(modelId: string, payload: LlmModel): Promise<LlmModel> {
  return request<LlmModel>(`${BASE}/admin/models/${modelId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function adminDeleteModel(modelId: string): Promise<{ status: string }> {
  return request(`${BASE}/admin/models/${modelId}`, { method: "DELETE" });
}

export interface ModelTestResult {
  ok: boolean;
  latency_ms: number;
  served_models: string[];
  matches?: boolean;
  error: string | null;
}

export async function adminTestModel(modelId: string): Promise<ModelTestResult> {
  return request<ModelTestResult>(`${BASE}/admin/models/${modelId}/test`, {
    method: "POST",
  });
}

// ─── MCP Servers (admin-managed dispatch targets) ──────────────────────────

export interface McpServer {
  id: string;
  name: string;
  description: string;
  transport: "mcp_http" | "webhook";
  url: string;
  auth_header: string | null;
  default_tool: string | null;
  timeout_s: number;
  enable_outbound: boolean;
  enable_tools: boolean;
  is_active: boolean;
  discovered_tools: Array<{ name?: string; description?: string; [k: string]: unknown }>;
  last_health: "ok" | "error" | "unknown" | string;
  last_error: string | null;
  last_checked_at: string | null;
  sort_order: number;
}

export type McpServerPayload = Omit<
  McpServer,
  "id" | "discovered_tools" | "last_health" | "last_error" | "last_checked_at"
>;

export interface McpTestResult {
  ok: boolean;
  tools: Array<{ name?: string; description?: string; [k: string]: unknown }>;
  error: string | null;
  latency_ms: number;
}

export async function adminListMcpServers(): Promise<McpServer[]> {
  return request<McpServer[]>(`${BASE}/admin/mcp-servers`);
}

export async function adminCreateMcpServer(payload: McpServerPayload): Promise<McpServer> {
  return request<McpServer>(`${BASE}/admin/mcp-servers`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateMcpServer(
  serverId: string,
  payload: McpServerPayload,
): Promise<McpServer> {
  return request<McpServer>(`${BASE}/admin/mcp-servers/${serverId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function adminDeleteMcpServer(serverId: string): Promise<{ status: string }> {
  return request(`${BASE}/admin/mcp-servers/${serverId}`, { method: "DELETE" });
}

export async function adminTestMcpServer(serverId: string): Promise<McpTestResult> {
  return request<McpTestResult>(`${BASE}/admin/mcp-servers/${serverId}/test`, {
    method: "POST",
  });
}

// ─── Research Backends (Web Araştırma Kaynakları) ────────────────────────

export interface ResearchBackend {
  id: string;
  name: string;
  display_name: string;
  description: string;
  is_builtin: boolean;
  enabled: boolean;
  priority: number;
  timeout_ms: number;
  max_results: number;
  requires_key: boolean;
  api_key_env: string | null;
  config: Record<string, unknown>;
  api_key_present: boolean;
  updated_at: string;
  // Live runtime status echoed by the backend so the UI can warn when a
  // toggle won't take effect (e.g. MACDM_DISABLE_DDG=1 keeps the
  // backend out of the registry entirely; flipping the DB flag won't
  // help). Optional for backwards-compat with older deploys.
  in_registry?: boolean;
  in_chain?: boolean;
  runtime_note?: string | null;
  /** İstihbarat Ajanı Faz 0 — admin-panel grouping: web/news/social/academic/media/custom. */
  category?: string;
}

export interface ResearchBackendCreatePayload {
  name: string;
  display_name: string;
  description?: string;
  enabled?: boolean;
  priority?: number;
  timeout_ms?: number;
  max_results?: number;
  requires_key?: boolean;
  api_key_env?: string | null;
  config?: Record<string, unknown>;
}

export interface ResearchBackendPatchPayload {
  display_name?: string;
  description?: string;
  enabled?: boolean;
  priority?: number;
  timeout_ms?: number;
  max_results?: number;
  requires_key?: boolean;
  api_key_env?: string | null;
  config?: Record<string, unknown>;
}

export interface ResearchBackendTestResult {
  name: string;
  ok: boolean;
  count?: number;
  elapsed_ms: number;
  reason?: string;
  /** İş Kolu D1 — set when ok=true but 0 results were caused by a
   * non-200 upstream response (e.g. Reddit's anti-bot 403), so the
   * admin can tell "network-blocked" apart from "no matching content". */
  warning?: string;
  results?: Array<{
    name: string;
    title: string;
    url: string;
    summary: string;
    published: string | null;
  }>;
}

export async function adminListResearchBackends(): Promise<ResearchBackend[]> {
  return request<ResearchBackend[]>(`${BASE}/admin/research-backends`);
}

export async function adminCreateResearchBackend(
  payload: ResearchBackendCreatePayload,
): Promise<ResearchBackend> {
  return request<ResearchBackend>(`${BASE}/admin/research-backends`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateResearchBackend(
  backendId: string,
  payload: ResearchBackendPatchPayload,
): Promise<ResearchBackend> {
  return request<ResearchBackend>(`${BASE}/admin/research-backends/${backendId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function adminDeleteResearchBackend(
  backendId: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`${BASE}/admin/research-backends/${backendId}`, {
    method: "DELETE",
  });
}

export async function adminTestResearchBackend(
  backendId: string,
  query: string = "yapay zeka",
): Promise<ResearchBackendTestResult> {
  const qs = encodeURIComponent(query);
  return request<ResearchBackendTestResult>(
    `${BASE}/admin/research-backends/${backendId}/test?query=${qs}`,
    { method: "POST" },
  );
}

// ─── History ──────────────────────────────────────────────────────────────

export interface DebateHistoryStats {
  total: number;
  finalized: number;
  running: number;
  cancelled: number;
  abandoned: number;
  avg_consensus: number | null; // 0-100 percentage
  avg_duration_s: number | null;
  avg_turns: number | null;
  total_turns: number;
  consensus_buckets: { high: number; mid: number; low: number; none: number };
  daily_trend: Array<{
    date: string;
    count: number;
    avg_consensus: number | null;
  }>;
  week_over_week: {
    count_pct: number | null;
    last_week_count: number;
    prior_week_count: number;
  };
}

export async function fetchDebateHistory(params?: {
  limit?: number;
  offset?: number;
  status?: string;
  q?: string;
  /**
   * Surface fork/duplicate "shell" sessions that never produced any
   * turns. Hidden by default to keep the dashboard a clean archive
   * rather than a drafts inbox. Set true when rendering an explicit
   * "Taslaklar" filter view.
   */
  include_empty?: boolean;
}): Promise<DebateHistoryItem[]> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  if (params?.status) qs.set("status", params.status);
  if (params?.q) qs.set("q", params.q);
  if (params?.include_empty) qs.set("include_empty", "true");
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<DebateHistoryItem[]>(`${BASE}/history${suffix}`);
}

export async function fetchDebateHistoryStats(): Promise<DebateHistoryStats> {
  return request<DebateHistoryStats>(`${BASE}/history/stats`);
}

export async function fetchDebateReplay(
  sessionId: string,
): Promise<DebateReplayData> {
  return request<DebateReplayData>(`${BASE}/history/${sessionId}`);
}

export async function fetchV2ResilienceSnapshot(
  sessionId: string,
): Promise<V2ResilienceSnapshot> {
  return request<V2ResilienceSnapshot>(`${BASE}/v2/history/${sessionId}/resilience`);
}

// ─── Cognitive Dispositions API ─────────────────────────────────────────

export interface CognitiveDisposition {
  id: string;
  slug: string;
  label_tr: string;
  description: string;
  icon_name: string;
  prompt_template: string;
  tool_affinity: number;
  sort_order: number;
  is_active: boolean;
}

export async function adminListDispositions(): Promise<CognitiveDisposition[]> {
  return request<CognitiveDisposition[]>(`${BASE}/admin/cognitive-dispositions`);
}

export async function adminCreateDisposition(
  body: Omit<CognitiveDisposition, "id">,
): Promise<{ id: string; slug: string }> {
  return request(`${BASE}/admin/cognitive-dispositions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function adminUpdateDisposition(
  id: string,
  body: Partial<Omit<CognitiveDisposition, "id">>,
): Promise<{ ok: boolean }> {
  return request(`${BASE}/admin/cognitive-dispositions/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function adminDeleteDisposition(id: string): Promise<{ ok: boolean }> {
  return request(`${BASE}/admin/cognitive-dispositions/${id}`, {
    method: "DELETE",
  });
}

// ─── Domain Objects API ─────────────────────────────────────────────────

export interface DomainObject {
  id: string;
  name: string;
  category: string;
  description: string;
  specs: Record<string, unknown>;
  icon_name: string;
  sort_order: number;
  is_active: boolean;
}

export async function adminListDomainObjects(): Promise<DomainObject[]> {
  return request<DomainObject[]>(`${BASE}/admin/domain-objects`);
}

export async function adminCreateDomainObject(
  body: Omit<DomainObject, "id">,
): Promise<{ id: string; name: string }> {
  return request(`${BASE}/admin/domain-objects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function adminUpdateDomainObject(
  id: string,
  body: Partial<Omit<DomainObject, "id">>,
): Promise<{ ok: boolean }> {
  return request(`${BASE}/admin/domain-objects/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function adminDeleteDomainObject(id: string): Promise<{ ok: boolean }> {
  return request(`${BASE}/admin/domain-objects/${id}`, {
    method: "DELETE",
  });
}

// ─── Debate Versioning API ────────────────────────────────────────────────

import type {
  DebateVersion,
  DebatePersonaSnapshot,
  ComparisonReport,
} from "../domain/types";

export interface ForkOptions {
  version_label?: string;
  persona_overrides?: Array<{
    seat_index: number;
    system_instr?: string;
    role?: string;
    expertise?: string;
    display_name?: string;
  }>;
  config_overrides?: {
    max_rounds?: number;
    critique_strictness?: string;
    agent_count?: number;
    auto_finalize?: boolean;
    user_debate_prompt?: string;
    user_moderator_prompt?: string;
    generation_seed?: number;
    topology?: string;
  };
}

export async function forkDebate(
  debateId: string,
  opts?: ForkOptions,
): Promise<{ debate_id: string; version_index: number }> {
  return request(`${BASE}/${debateId}/fork`, {
    method: "POST",
    body: JSON.stringify(opts ?? {}),
  });
}

export async function duplicateDebate(
  debateId: string,
): Promise<{ debate_id: string }> {
  return request(`${BASE}/${debateId}/duplicate`, { method: "POST" });
}

/**
 * RerunOptions — superset of ForkOptions but with a stronger contract.
 *
 * Rerun is the user-facing "Yeni Versiyonu Çalıştır" workflow that
 * replaces the old fork-then-wizard dance. The backend performs an
 * atomic fork + persona clone (or persona-regen flag) inside a
 * single transaction, so the FE can immediately auto-start the
 * brand-new debate row without intermediate manual steps.
 */
export interface RerunOptions {
  version_label?: string;
  agent_count?: number;
  max_rounds?: number;
  model_id?: string;
  temperature?: number;
  critique_strictness?: "loose" | "standard" | "strict";
  auto_finalize?: boolean;
  /**
   * When true the backend skips the persona snapshot clone and
   * tags the rerun row as "persona regeneration requested". The FE
   * must then call the wizard generator before triggering start —
   * the dashboard CTA wires this branching logic for the user.
   */
  regenerate_personas?: boolean;
}

export interface RerunResponse {
  debate_id: string;
  version_index: number;
  regenerate_personas: boolean;
  config: {
    agent_count: number | null;
    max_rounds: number | null;
    model_id: string | null;
    temperature: number | null;
  };
}

export async function rerunDebate(
  debateId: string,
  opts: RerunOptions,
): Promise<RerunResponse> {
  return request(`${BASE}/${debateId}/rerun`, {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

/**
 * Loads the lineage tree for a debate.
 *
 * Returns an empty tree on 404 instead of throwing — the orchestrator
 * legitimately responds 404 when the debate has never been forked or
 * when the caller is looking at a session created before the
 * versioning subsystem was deployed. Both cases should degrade
 * gracefully into "single-version" UI rather than tearing down the
 * whole replay panel.
 */
export async function loadVersionTree(
  debateId: string,
): Promise<{ root_id: string; tree: DebateVersion[] }> {
  try {
    return await request(`${BASE}/${debateId}/versions`);
  } catch (e) {
    const msg = String((e as Error)?.message ?? e);
    if (/\b404\b|not[_\s-]?found/i.test(msg)) {
      return { root_id: debateId, tree: [] };
    }
    throw e;
  }
}

export async function fetchDebatePersonas(
  debateId: string,
): Promise<DebatePersonaSnapshot[]> {
  return request<DebatePersonaSnapshot[]>(`${BASE}/${debateId}/personas`);
}

export async function compareDebates(
  debateAId: string,
  debateBId: string,
  force?: boolean,
): Promise<ComparisonReport> {
  return request<ComparisonReport>(`${BASE}/compare`, {
    method: "POST",
    body: JSON.stringify({
      debate_a_id: debateAId,
      debate_b_id: debateBId,
      force: force ?? false,
    }),
  });
}

// ─── Editorial Commentary (default pair + extra notes) ────────────────────

export type CommentaryNoteKind = "ai_extra" | "user_note";

export interface CommentaryNote {
  id: string;
  debateId: string;
  kind: CommentaryNoteKind;
  title: string | null;
  customPrompt: string | null;
  content: string;
  modelId: string | null;
  createdBy: string;
  createdAt: string | null;
}

export interface GenerateCommentaryOptions {
  /** Force regeneration even if a default pair already exists. */
  force?: boolean;
  /** Override the admin-configured commentary model for this call only. */
  modelId?: string | null;
  /** Operator focus instructions appended to the system prompt. */
  customPrompt?: string | null;
}

/** Fire the default analysis/critique pair generation. Writes into `verdicts`. */
export async function generateCommentary(
  debateId: string,
  opts?: GenerateCommentaryOptions,
): Promise<{ commentaryAnalysis: string | null; commentaryCritique: string | null }> {
  const body: Record<string, unknown> = {};
  if (opts?.force) body.force = true;
  if (opts?.modelId) body.modelId = opts.modelId;
  if (opts?.customPrompt) body.customPrompt = opts.customPrompt;
  return request(`${BASE}/${debateId}/commentary`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Hard-clear the default analysis/critique pair on the verdict. */
export async function deleteDefaultCommentary(
  debateId: string,
): Promise<{ status: string; wasPresent: boolean }> {
  return request(`${BASE}/${debateId}/commentary`, { method: "DELETE" });
}

/** List all extra commentary notes for a debate, newest first. */
export async function listCommentaryNotes(
  debateId: string,
): Promise<CommentaryNote[]> {
  const res = await request<{ notes: CommentaryNote[] }>(
    `${BASE}/${debateId}/commentary/notes`,
  );
  return res.notes;
}

export interface AddCommentaryNotePayload {
  kind: CommentaryNoteKind;
  /** Required for `user_note`. */
  content?: string;
  /** Required for `ai_extra` — operator focus instructions. */
  customPrompt?: string;
  /** Optional model override (otherwise admin default). */
  modelId?: string | null;
  /** Optional title; auto-derived from customPrompt for ai_extra. */
  title?: string;
}

export async function addCommentaryNote(
  debateId: string,
  payload: AddCommentaryNotePayload,
): Promise<CommentaryNote> {
  const res = await request<{ note: CommentaryNote }>(
    `${BASE}/${debateId}/commentary/notes`,
    { method: "POST", body: JSON.stringify(payload) },
  );
  return res.note;
}

export async function deleteCommentaryNote(
  debateId: string,
  noteId: string,
): Promise<{ status: string; id: string }> {
  return request(`${BASE}/${debateId}/commentary/notes/${noteId}`, {
    method: "DELETE",
  });
}

// ─── Görev 21 — verdict review (Approve / Conditional / Reject) ───────────
//
// FE surface for the plan's "insan onay akışı". The endpoint itself
// (`POST /api/debate/{id}/review`) is part of the verdict-state-machine
// backend work (Görev 21) and may not exist on every deployment yet —
// `submitVerdictReview` is written defensively (via the shared
// `request()` helper, which already turns any non-2xx into a thrown
// `Error`) so `ReviewActions` can show a clean "not available yet"
// toast instead of crashing when the route 404s.
export type ReviewDecision = "approve" | "conditional" | "reject";

export interface ReviewSubmission {
  decision: ReviewDecision;
  note?: string;
}

/** `POST /{id}/review` gövdesi. Anahtarlar `routes_review.py`'nin
 *  döndürdüğüyle birebirdir — burada bir zamanlar `ok`/`approvalStatus`
 *  yazıyordu, ikisi de hiç gönderilmiyor. `verdictStatus` sunucunun
 *  yetkili yeni durumudur; çağıran onu doğrudan store'a yazmalı. */
export interface ReviewResult {
  sessionId: string;
  reviewId: string;
  decision: ReviewDecision;
  verdictStatus: Record<string, unknown>;
}

export async function submitVerdictReview(
  debateId: string,
  payload: ReviewSubmission,
): Promise<ReviewResult> {
  return request(`${BASE}/${debateId}/review`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
